"use client";

import { selectAuthToken, useAuthStore } from "../../store/useAuth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { buildApiUrl, MutationConfig } from "./hook-utils";
import { API_ENDPOINTS } from "../../config/api-endpoints";
import { handleAuthError } from "../../utils/auth-error-handler";

// Types for upload responses - Updated to match actual API response
export interface UploadResponse {
  message: string;
  data: {
    url: string;
    key: string;
    originalName: string;
    size: number;
    mimetype: string;
  };
}

export interface MultipleUploadResponse {
  message: string;
  data: {
    successful: Array<{
      url: string;
      key: string;
      originalName: string;
      size: number;
      mimetype: string;
    }>;
    failed: Array<{
      originalName: string;
      error: string;
    }>;
  };
}

export interface PresignedUrlResponse {
  message: string;
  data: {
    url: string;
    key: string;
  };
}

export interface DeleteResponse {
  message: string;
  data: {
    message: string;
  };
}

// Upload mutation configurations
export interface UploadMutationConfig extends MutationConfig {
  folder?: string;
}

export interface PresignedUrlMutationConfig extends MutationConfig {
  expiresIn?: number;
}

export interface DeleteMutationConfig extends MutationConfig {
  byUrl?: boolean;
}

const getUploadError = async (
  response: Response,
  fallback: string
): Promise<Error> => {
  try {
    const body = await response.json();
    const message =
      typeof body?.message === "string" && body.message.trim()
        ? body.message
        : fallback;

    const error = new Error(message);
    (error as Error & { status: number }).status = response.status;
    return error;
  } catch {
    const error = new Error(fallback);
    (error as Error & { status: number }).status = response.status;
    return error;
  }
};

// Single file upload mutation
export const useUploadSingle = (config?: UploadMutationConfig) => {
  const token = useAuthStore(selectAuthToken);
  const queryClient = useQueryClient();

  return useMutation<UploadResponse, Error, { file: File; folder?: string }>({
    mutationFn: async ({ file, folder = config?.folder || "uploads" }) => {
      const url = buildApiUrl(API_ENDPOINTS.UPLOAD.SINGLE);

      if (!url || !token) {
        throw new Error("URL or token not available");
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", folder);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-Admin-Authorization": `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw await getUploadError(
          response,
          `Upload failed (HTTP ${response.status})`
        );
      }

      const result = await response.json();
      return result;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["uploads"] });
      config?.onSuccess?.(data);
    },
    onError: (error, variables, context) => {
      handleAuthError(error);
      if (config?.onError) {
        config.onError(error);
        return;
      }
      const message = (error as any)?.message || "Upload failed";
      toast.error(message);
    },
    ...(config?.onMutate && { onMutate: config.onMutate }),
  });
};

// Multiple files upload mutation
export const useUploadMultiple = (config?: UploadMutationConfig) => {
  const token = useAuthStore(selectAuthToken);
  const queryClient = useQueryClient();

  return useMutation<
    MultipleUploadResponse,
    Error,
    { files: File[]; folder?: string }
  >({
    mutationFn: async ({ files, folder = config?.folder || "uploads" }) => {
      const url = buildApiUrl(API_ENDPOINTS.UPLOAD.MULTIPLE);

      if (!url || !token) {
        throw new Error("URL or token not available");
      }

      const formData = new FormData();
      files.forEach((file) => {
        formData.append("files", file);
      });
      formData.append("folder", folder);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-Admin-Authorization": `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw await getUploadError(
          response,
          `Upload failed (HTTP ${response.status})`
        );
      }

      const result = await response.json();
      return result;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["uploads"] });
      config?.onSuccess?.(data);
    },
    onError: (error, variables, context) => {
      handleAuthError(error);
      if (config?.onError) {
        config.onError(error);
        return;
      }
      const message = (error as any)?.message || "Upload failed";
      toast.error(message);
    },
    ...(config?.onMutate && { onMutate: config.onMutate }),
  });
};

// Generate presigned URL mutation
export const useGeneratePresignedUrl = (
  config?: PresignedUrlMutationConfig
) => {
  const token = useAuthStore(selectAuthToken);
  const queryClient = useQueryClient();

  return useMutation<
    PresignedUrlResponse,
    Error,
    { key: string; expiresIn?: number }
  >({
    mutationFn: async ({ key, expiresIn = config?.expiresIn || 3600 }) => {
      const url = buildApiUrl(API_ENDPOINTS.UPLOAD.PRESIGNED_URL);

      if (!url || !token) {
        throw new Error("URL or token not available");
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          key,
          expiresIn,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["uploads"] });
      config?.onSuccess?.(data);
    },
    onError: (error, variables, context) => {
      if (config?.onError) {
        config.onError(error);
        return;
      }
      const message =
        (error as any)?.message || "Failed to generate presigned URL";
      toast.error(message);
    },
    ...(config?.onMutate && { onMutate: config.onMutate }),
  });
};

// Upload with presigned URL mutation (client-side upload)
export const useUploadWithPresignedUrl = (config?: MutationConfig) => {
  return useMutation<boolean, Error, { file: File; presignedUrl: string }>({
    mutationFn: async ({ file, presignedUrl }) => {
      try {
        const response = await fetch(presignedUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
        });

        return response.ok;
      } catch (error) {
        // Error uploading with presigned URL
        return false;
      }
    },
    onSuccess: (data, variables, context) => {
      config?.onSuccess?.(data);
    },
    onError: (error, variables, context) => {
      if (config?.onError) {
        config.onError(error);
        return;
      }
      const message =
        (error as any)?.message || "Upload with presigned URL failed";
      toast.error(message);
    },
    ...(config?.onMutate && { onMutate: config.onMutate }),
  });
};

// Delete file by key mutation
export const useDeleteFileByKey = (config?: DeleteMutationConfig) => {
  const token = useAuthStore(selectAuthToken);
  const queryClient = useQueryClient();

  return useMutation<DeleteResponse, Error, string>({
    mutationFn: async (key: string) => {
      const url = buildApiUrl(`/upload/${encodeURIComponent(key)}`);

      if (!url || !token) {
        throw new Error("URL or token not available");
      }

      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Authorization": `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["uploads"] });
      config?.onSuccess?.(data);
    },
    onError: (error, variables, context) => {
      if (config?.onError) {
        config.onError(error);
        return;
      }
      const message = (error as any)?.message || "Failed to delete file";
      toast.error(message);
    },
    ...(config?.onMutate && { onMutate: config.onMutate }),
  });
};

// Delete file by URL mutation
export const useDeleteFileByUrl = (config?: DeleteMutationConfig) => {
  const token = useAuthStore(selectAuthToken);
  const queryClient = useQueryClient();

  return useMutation<DeleteResponse, Error, string>({
    mutationFn: async (url: string) => {
      const apiUrl = buildApiUrl(API_ENDPOINTS.UPLOAD.DELETE_BY_URL);

      if (!apiUrl || !token) {
        throw new Error("URL or token not available");
      }

      const response = await fetch(apiUrl, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ url }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["uploads"] });
      config?.onSuccess?.(data);
    },
    onError: (error, variables, context) => {
      if (config?.onError) {
        config.onError(error);
        return;
      }
      const message = (error as any)?.message || "Failed to delete file";
      toast.error(message);
    },
    ...(config?.onMutate && { onMutate: config.onMutate }),
  });
};
